/** @format */

import { isCountrySupported, SUPPORTED_COUNTRIES } from "./stripeConnectCountries";

/**
 * Mirror of server/src/utility/circleCountryEligibility.ts. Keep the two in
 * sync: the server is the gate, this only lets the UI explain it up front.
 */

export type CountryEligibilityReason =
    | "ok"
    | "missing_country"
    | "unsupported_country"
    | "conflicting_countries"
    | "country_mismatch";

export interface CountryEligibility {
    eligible: boolean;
    reason: CountryEligibilityReason;
    userCountries: string[];
    circleCountry: string;
}

type CountryProfile =
    | {
        phone?: { country?: string | null } | null;
        address?: { country?: string | null } | null;
    }
    | null
    | undefined;

type CircleLike =
    | { configuration?: { country?: string | null } | null }
    | null
    | undefined;

// Same aliases as the server's countryNameToCode.
const NAME_ALIASES: Record<string, string> = {
    usa: "US",
    "united states of america": "US",
    america: "US",
    uk: "GB",
    "great britain": "GB",
    england: "GB",
    "england, uk": "GB",
    deutschland: "DE",
    "the netherlands": "NL",
    holland: "NL",
    uae: "AE",
    "south korea": "KR",
    korea: "KR",
    "ivory coast": "CI",
    "cote d'ivoire": "CI",
    "côte d'ivoire": "CI",
    "trinidad & tobago": "TT",
    saudi: "SA",
    ksa: "SA",
};

const toCountryCode = (value?: string | null): string | null => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (!trimmed) return null;
    const upper = trimmed.toUpperCase();
    if (upper.length === 2 && isCountrySupported(upper)) return upper;
    const lower = trimmed.toLowerCase();
    const byName = SUPPORTED_COUNTRIES.find((c) => c.name.toLowerCase() === lower);
    // Unknown 2-letter values stay as-is so they surface as "unsupported", not "missing".
    return byName?.code ?? NAME_ALIASES[lower] ?? (upper.length === 2 ? upper : null);
};

export const getUserCountrySignals = (user: CountryProfile): string[] => {
    const codes = [
        toCountryCode(user?.address?.country),
        toCountryCode(user?.phone?.country),
    ].filter((code): code is string => Boolean(code));
    return Array.from(new Set(codes));
};

export const resolveCircleCountry = (circle: CircleLike): string =>
    circle?.configuration?.country?.trim().toUpperCase() || "US";

export const checkCircleCountryEligibility = (
    user: CountryProfile,
    circle: CircleLike,
): CountryEligibility => {
    const userCountries = getUserCountrySignals(user);
    const circleCountry = resolveCircleCountry(circle);
    const result = (reason: CountryEligibilityReason): CountryEligibility => ({
        eligible: reason === "ok",
        reason,
        userCountries,
        circleCountry,
    });

    if (userCountries.length === 0) return result("missing_country");
    if (userCountries.some((code) => !isCountrySupported(code))) {
        return result("unsupported_country");
    }
    if (userCountries.length > 1) return result("conflicting_countries");
    if (userCountries[0] !== circleCountry) return result("country_mismatch");
    return result("ok");
};

export const getCountryEligibilityMessage = (e: CountryEligibility): string => {
    switch (e.reason) {
        case "missing_country":
            return "Please update your profile with your country before joining a circle.";
        case "unsupported_country":
            return "Your country is not yet supported for circles. We are working on expanding availability.";
        case "conflicting_countries":
            return `Your profile lists more than one country (${e.userCountries.join(" and ")}). Your address and phone number must both be in the country you live in before you can join a circle.`;
        case "country_mismatch":
            return `You can only join circles based in your country (${e.userCountries[0]}). This circle is based in ${e.circleCountry}.`;
        default:
            return "";
    }
};
